import { Loader2, MapPin } from "lucide-react";
import { type ChangeEvent, useEffect, useId, useMemo, useRef, useState } from "react";

import type { ClientAddressFields } from "@/api/types";

/**
 * Structured address input with optional Google Places autocomplete.
 *
 * Flow:
 *  1. User types in line1 → debounced POST to Places Autocomplete (New)
 *     with a session token (cuts billing — Place Details under the same
 *     session token is free).
 *  2. Suggestions render in a popover. User picks one → GET
 *     /v1/places/{id} for ``addressComponents``, map onto our six
 *     fields, fire onChange with the merged value.
 *  3. User can edit any field afterward (incl. line1) — autocomplete
 *     is a starting point, not a constraint.
 *
 * Graceful degradation: when ``VITE_GOOGLE_PLACES_API_KEY`` isn't set,
 * the search dropdown stays hidden and only the structured fields
 * render. Manual entry always works.
 */

const PLACES_KEY = (import.meta.env.VITE_GOOGLE_PLACES_API_KEY as string | undefined) ?? "";
const PLACES_ENABLED = PLACES_KEY.length > 0;

interface AddressInputProps {
  value: ClientAddressFields;
  onChange: (value: ClientAddressFields) => void;
}

export function AddressInput({ value, onChange }: AddressInputProps) {
  const baseId = useId();

  function patch(partial: Partial<ClientAddressFields>) {
    onChange({ ...value, ...partial });
  }

  return (
    <div className="space-y-3">
      {PLACES_ENABLED ? (
        <PlacesSearch
          line1={value.address_line1 ?? ""}
          onLine1Change={(line1) => patch({ address_line1: line1 || null })}
          onPlacePicked={(picked) => onChange({ ...value, ...picked })}
        />
      ) : (
        <label className="block">
          <span className="label-mono">Address line 1</span>
          <input
            type="text"
            value={value.address_line1 ?? ""}
            onChange={(e) => patch({ address_line1: e.target.value || null })}
            placeholder="1428 Maple Ave"
            className="mt-1 w-full rounded border border-rule bg-paper px-2 py-1.5 text-sm text-graphite placeholder:text-graphite-faint focus:border-graphite focus:outline-none"
          />
        </label>
      )}

      <label className="block">
        <span className="label-mono">Address line 2 (optional)</span>
        <input
          type="text"
          value={value.address_line2 ?? ""}
          onChange={(e) => patch({ address_line2: e.target.value || null })}
          placeholder="Apt 3B / Suite 200 / …"
          className="mt-1 w-full rounded border border-rule bg-paper px-2 py-1.5 text-sm text-graphite placeholder:text-graphite-faint focus:border-graphite focus:outline-none"
        />
      </label>

      <div className="grid grid-cols-1 gap-3 md:grid-cols-[1fr_120px_140px]">
        <label className="block">
          <span className="label-mono">City</span>
          <input
            type="text"
            value={value.city ?? ""}
            onChange={(e) => patch({ city: e.target.value || null })}
            placeholder="Charlotte"
            id={`${baseId}-city`}
            className="mt-1 w-full rounded border border-rule bg-paper px-2 py-1.5 text-sm text-graphite placeholder:text-graphite-faint focus:border-graphite focus:outline-none"
          />
        </label>

        <label className="block">
          <span className="label-mono">State / region</span>
          <input
            type="text"
            value={value.region ?? ""}
            onChange={(e) => patch({ region: e.target.value.toUpperCase() || null })}
            placeholder="NC"
            maxLength={63}
            className="mt-1 w-full rounded border border-rule bg-paper px-2 py-1.5 font-mono text-sm uppercase text-graphite placeholder:text-graphite-faint focus:border-graphite focus:outline-none"
          />
        </label>

        <label className="block">
          <span className="label-mono">ZIP / postal</span>
          <input
            type="text"
            value={value.postal_code ?? ""}
            onChange={(e) => patch({ postal_code: e.target.value || null })}
            placeholder="28204"
            className="mt-1 w-full rounded border border-rule bg-paper px-2 py-1.5 font-mono text-sm text-graphite placeholder:text-graphite-faint focus:border-graphite focus:outline-none"
          />
        </label>
      </div>

      <label className="block">
        <span className="label-mono">Country (ISO 2-letter, e.g. US)</span>
        <input
          type="text"
          value={value.country ?? ""}
          onChange={(e) => patch({ country: e.target.value.toUpperCase() || null })}
          placeholder="US"
          maxLength={2}
          className="mt-1 w-32 rounded border border-rule bg-paper px-2 py-1.5 font-mono text-sm uppercase text-graphite placeholder:text-graphite-faint focus:border-graphite focus:outline-none"
        />
      </label>
    </div>
  );
}

// ---------------------------------------------------------------
// Google Places (New) Autocomplete
// ---------------------------------------------------------------

interface PlaceSuggestion {
  placeId: string;
  mainText: string;
  secondaryText: string;
}

function PlacesSearch({
  line1,
  onLine1Change,
  onPlacePicked,
}: {
  line1: string;
  onLine1Change: (value: string) => void;
  onPlacePicked: (fields: ClientAddressFields) => void;
}) {
  const [suggestions, setSuggestions] = useState<PlaceSuggestion[]>([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [highlight, setHighlight] = useState(-1);
  const containerRef = useRef<HTMLDivElement | null>(null);

  // Session token — same UUID for the whole "user is searching"
  // interaction. Google bills Place Details for free if it's under
  // the same session as the autocomplete that returned the placeId.
  const sessionToken = useMemo(
    () =>
      typeof crypto !== "undefined" && "randomUUID" in crypto
        ? crypto.randomUUID()
        : `${Date.now()}-${Math.random()}`,
    [],
  );

  // Debounce queries to keep call volume + spend low.
  useEffect(() => {
    const query = line1.trim();
    if (query.length < 3) {
      setSuggestions([]);
      setOpen(false);
      return;
    }
    const handle = setTimeout(async () => {
      setLoading(true);
      try {
        const items = await fetchAutocomplete(query, sessionToken);
        setSuggestions(items);
        setOpen(items.length > 0);
        setHighlight(-1);
      } catch {
        setSuggestions([]);
      } finally {
        setLoading(false);
      }
    }, 220);
    return () => clearTimeout(handle);
  }, [line1, sessionToken]);

  // Click-outside dismiss.
  useEffect(() => {
    function onDocClick(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, []);

  async function pick(suggestion: PlaceSuggestion) {
    setOpen(false);
    setLoading(true);
    try {
      const fields = await fetchPlaceDetails(suggestion.placeId, sessionToken);
      onPlacePicked(fields);
    } catch {
      /* keep whatever the user already typed */
    } finally {
      setLoading(false);
    }
  }

  function onKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (!open || suggestions.length === 0) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlight((h) => Math.min(h + 1, suggestions.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlight((h) => Math.max(h - 1, -1));
    } else if (e.key === "Enter") {
      if (highlight >= 0) {
        e.preventDefault();
        const s = suggestions[highlight];
        if (s) void pick(s);
      }
    } else if (e.key === "Escape") {
      setOpen(false);
    }
  }

  return (
    <div ref={containerRef} className="relative">
      <label className="block">
        <span className="label-mono">Address line 1</span>
        <span className="relative mt-1 flex items-center">
          <MapPin className="absolute left-2 h-3.5 w-3.5 text-graphite-soft" aria-hidden />
          <input
            type="text"
            value={line1}
            onChange={(e: ChangeEvent<HTMLInputElement>) => onLine1Change(e.target.value)}
            onFocus={() => suggestions.length > 0 && setOpen(true)}
            onKeyDown={onKeyDown}
            placeholder="Start typing — 1428 Maple Ave…"
            autoComplete="off"
            className="w-full rounded border border-rule bg-paper py-1.5 pl-7 pr-9 text-sm text-graphite placeholder:text-graphite-faint focus:border-graphite focus:outline-none"
          />
          {loading && (
            <Loader2
              className="absolute right-2 h-3.5 w-3.5 animate-spin text-graphite-soft"
              aria-hidden
            />
          )}
        </span>
      </label>

      {open && suggestions.length > 0 && (
        <ul className="absolute z-20 mt-1 w-full overflow-hidden rounded-lg border border-rule bg-paper-warm shadow-lg">
          {suggestions.map((s, idx) => (
            <li key={s.placeId}>
              <button
                type="button"
                // biome-ignore lint/a11y/useSemanticElements: native <option> only works inside <select>; this is the WAI-ARIA combobox/listbox suggestion pattern with a button for click + keyboard activation.
                role="option"
                aria-selected={idx === highlight}
                onMouseEnter={() => setHighlight(idx)}
                onMouseDown={(e) => {
                  // mousedown so the input's blur doesn't dismiss
                  // the popover before pick() runs.
                  e.preventDefault();
                  void pick(s);
                }}
                className={[
                  "flex w-full items-start gap-2 px-3 py-2 text-left transition",
                  idx === highlight ? "bg-pencil-yellow-soft" : "hover:bg-pencil-yellow-soft/60",
                ].join(" ")}
              >
                <MapPin className="mt-0.5 h-3.5 w-3.5 flex-none text-graphite-soft" aria-hidden />
                <div className="min-w-0">
                  <div className="truncate text-sm text-graphite">{s.mainText}</div>
                  <div className="truncate font-mono text-xs text-graphite-soft">
                    {s.secondaryText}
                  </div>
                </div>
              </button>
            </li>
          ))}
        </ul>
      )}

      <p className="mt-1 font-mono text-[10px] text-graphite-faint">
        Typing here searches addresses · pick a suggestion to fill the rest
      </p>
    </div>
  );
}

// ---------------------------------------------------------------
// Google Places API helpers
// ---------------------------------------------------------------

async function fetchAutocomplete(query: string, sessionToken: string): Promise<PlaceSuggestion[]> {
  const resp = await fetch("https://places.googleapis.com/v1/places:autocomplete", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "X-Goog-Api-Key": PLACES_KEY,
    },
    body: JSON.stringify({
      input: query,
      sessionToken,
      includedRegionCodes: ["us"],
      includedPrimaryTypes: ["street_address", "premise", "subpremise"],
    }),
  });
  if (!resp.ok) return [];
  const data = (await resp.json()) as {
    suggestions?: Array<{
      placePrediction?: {
        placeId?: string;
        structuredFormat?: {
          mainText?: { text?: string };
          secondaryText?: { text?: string };
        };
        text?: { text?: string };
      };
    }>;
  };
  const out: PlaceSuggestion[] = [];
  for (const item of data.suggestions ?? []) {
    const p = item.placePrediction;
    if (!p?.placeId) continue;
    out.push({
      placeId: p.placeId,
      mainText: p.structuredFormat?.mainText?.text ?? p.text?.text ?? "(unknown)",
      secondaryText: p.structuredFormat?.secondaryText?.text ?? "",
    });
  }
  return out;
}

interface AddressComponent {
  longText: string;
  shortText: string;
  types: string[];
}

async function fetchPlaceDetails(
  placeId: string,
  sessionToken: string,
): Promise<ClientAddressFields> {
  const url = `https://places.googleapis.com/v1/places/${encodeURIComponent(placeId)}?sessionToken=${encodeURIComponent(sessionToken)}`;
  const resp = await fetch(url, {
    method: "GET",
    headers: {
      "X-Goog-Api-Key": PLACES_KEY,
      // FieldMask controls billing — only fetch what we use.
      "X-Goog-FieldMask": "addressComponents",
    },
  });
  if (!resp.ok) {
    return emptyAddress();
  }
  const data = (await resp.json()) as { addressComponents?: AddressComponent[] };
  return mapAddressComponents(data.addressComponents ?? []);
}

/** Map Google's flat addressComponents list onto our six structured
 * fields. line1 is street_number + route concatenated; line2 is
 * subpremise (apt/unit) when present. */
function mapAddressComponents(components: AddressComponent[]): ClientAddressFields {
  const byType = new Map<string, AddressComponent>();
  for (const c of components) {
    for (const t of c.types) {
      // First wins — Google's order is normally most-specific first.
      if (!byType.has(t)) byType.set(t, c);
    }
  }

  const streetNumber = byType.get("street_number")?.longText ?? "";
  const route = byType.get("route")?.longText ?? "";
  const subpremise = byType.get("subpremise")?.longText ?? "";

  const line1 = [streetNumber, route].filter(Boolean).join(" ").trim() || null;
  const line2 = subpremise || null;

  return {
    address_line1: line1,
    address_line2: line2,
    city:
      byType.get("locality")?.longText ??
      byType.get("postal_town")?.longText ??
      byType.get("sublocality")?.longText ??
      null,
    region: byType.get("administrative_area_level_1")?.shortText ?? null,
    postal_code: byType.get("postal_code")?.longText ?? null,
    country: byType.get("country")?.shortText?.toUpperCase() ?? null,
  };
}

function emptyAddress(): ClientAddressFields {
  return {
    address_line1: null,
    address_line2: null,
    city: null,
    region: null,
    postal_code: null,
    country: null,
  };
}
